import React, { useEffect, useRef } from 'react';
import { Icons, SVG } from '../../../components/atoms/SVG';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import { GlobalDocumentIds } from '../../../types/GlobalDocumentIds';
import debounce from '../../../lib/performance/debounce';
import { Tooltip, TooltipPlacement } from '../../../components/atoms/Tooltip';

/**
 * Arrow to jump to top via anchor link.
 * */
export function ReturnToTopArrow() {
  const { t } = useSonTranslation('glossary');
  const anchorRef = useRef<HTMLAnchorElement>(null);
  const moveUpClass = 'return-to-top-arrow__move-up';
  const moveDownClass = 'return-to-top-arrow__move-down';
  const isInReducedMotion = (): boolean => window.matchMedia(`(prefers-reduced-motion: reduce)`).matches;

  /**
   * Depending on the scroll position, toggle the hidden state of the anchor.
   * - small debounce to only calc when scroll has ended, to get a smooth up and down animation
   */
  const toggleAnchorHiddenState = debounce(() => {
    const contentElement = document.getElementById(GlobalDocumentIds.scrollContent);
    if (contentElement) {
      const scrollBarAtTop = contentElement.scrollTop < 200;

      if (anchorRef.current) {
        const { current } = anchorRef;
        if (scrollBarAtTop) {
          if (isInReducedMotion()) {
            // hide arrow without animation here, as onAnimationEnd will not fire in reduced motion
            current.classList.remove(moveUpClass);
            current.setAttribute('hidden', '');
          } else {
            current.classList.remove(moveUpClass);
            current.classList.add(moveDownClass);
          }
        } else {
          current.removeAttribute('hidden');
          current.classList.remove(moveDownClass);
          current.classList.add(moveUpClass);
        }
      }
    }
  }, 50);

  useEffect(() => {
    toggleAnchorHiddenState(); // call once to calc on mount, then add to eventlisteners
    const contentElement = document.getElementById(GlobalDocumentIds.scrollContent);
    if (contentElement) {
      contentElement.addEventListener('scroll', toggleAnchorHiddenState);
    }
    return () => {
      if (contentElement) {
        contentElement.removeEventListener('scroll', toggleAnchorHiddenState);
      }
    };
  }, [toggleAnchorHiddenState]);

  const setClassesAfterAnimationEnd = () => {
    if (anchorRef.current?.classList.contains(moveDownClass)) {
      anchorRef.current.classList.remove(moveDownClass);
      anchorRef.current?.setAttribute('hidden', '');
    }
  };

  return (
    <Tooltip attachTo={anchorRef} placement={TooltipPlacement.TOP} hideTooltipFromScreenReader>
      <a
        ref={anchorRef}
        onAnimationEnd={setClassesAfterAnimationEnd}
        href={`#${GlobalDocumentIds.scrollTopAnchor}`}
        title={t('upArrow')}
        className="return-to-top-arrow"
        hidden
      >
        <span className="invisible">{t('upArrow')}</span>
        <SVG svgSpriteId={Icons.UP_ARROW} svgClass="return-to-top-arrow__icon" />
      </a>
    </Tooltip>
  );
}
